import { calculateActivationTotalForProduct } from './calculateActivationTotalForProduct.mjs'
import { IsForeignCurrencyQuote } from './helpers.mjs'

/**
 * Calculates the total annual price for a quote, given the monthly product pricing and relevant quote details.
 *
 * @param {MonthlyPrice[]} productCoreMonthlyCosts - Monthly pricing for Core product.
 * @param {MonthlyPrice[]} productPerformanceMonthlyCosts - Monthly pricing for Performance product.
 * @param {MonthlyPrice[]} productTimeTrackingMonthlyCosts - Monthly pricing for Time Tracking product.
 * @param {MonthlyPrice[]} productBenefitsAdministrationMonthlyCosts - Monthly pricing for Benefits Administration product.
 * @param {MonthlyPrice[]} AdditionalJobsCosts - Monthly pricing for additional jobs.
 * @param {MonthlyPrice[]} AdditionalStorageCosts - Monthly pricing for additional storage.
 * @param {MonthlyPrice[]} TestAccountCosts - Monthly pricing for test accounts.
 * @param {MonthlyPrice[]} MonthlyTaxCosts - Monthly pricing for monthly taxes.
 * @param {MonthlyPrice[]} TimeClockMonthlyCost - Monthly pricing for time clocks.
 * @param {MonthlyPrice[]} payrollMonthlyTotal - Monthly pricing for payroll.
 * @param {MonthlyPrice[]} ConsultingServicesCosts - Monthly pricing for consulting services.
 * @param {any[]} product_list - Array of available products.
 * @param {Record<string, any>} discount_list - Object mapping discount identifiers to their details.
 * @param {string|number} book_id - Identifier for the book in the system.
 * @param {string} quoteType - The type of quote being calculated.
 * @param {boolean} coreProductSelected - Flag indicating if the core product is selected.
 * @param {number} productCoreEECount - Core employee count for the product.
 * @param {number} productPerformanceEECount - Performance employee count for the product.
 * @param {number} productTimeTrackingEECount - Time tracking employee count for the product.
 * @param {boolean} hasTimeClocks - Flag indicating if time clocks are enabled.
 * @param {any} productTimeClockDetails - Details for time clock configuration.
 * @param {number} productBenefitsAdministrationEECount - Benefits administration employee count.
 * @param {number} productPayrollEINCount - Payroll EIN count.
 * @param {number} productPayrollEECount - Payroll employee count.
 * @param {any} productPayrollEECounts - Additional payroll employee counts (complex structure).
 * @param {number} productPayrollTotalStateCount - Total state count for payroll.
 * @param {any} productPayrollStateCounts - Payroll state counts (array or object).
 * @param {any} professionalServiceDetails - Details for professional services.
 * @param {any} consultingServiceDetails - Details for consulting services.
 * @param {string} impType - Implementation type for the product.
 * @param {string} benAdminImpType - Benefits administration implementation type.
 * @param {string} payrollImpType - Payroll implementation type.
 * @param {any} discountBundle - Bundle discount details.
 * @param {boolean} discountNonProfit - Indicates if a non-profit discount applies.
 * @param {number} prepayLength - Prepayment length in months (1 = month-to-month).
 * @param {boolean} discountMultiEntity - Indicates if a multi-entity discount applies.
 * @param {boolean} discountHRMonthlyPromotion - Indicates if a monthly promotion discount applies.
 * @param {boolean} discountHRDealDeskMonthly - Indicates if a deal desk monthly discount applies.
 * @param {boolean} discountReseller - Indicates if a reseller discount applies.
 * @param {boolean} discountQuickstartTraining - Indicates if a quickstart training discount applies.
 * @param {boolean} discountImpPromotion - Indicates if an implementation promotion discount applies.
 * @param {boolean} discountDealDeskHRImp - Indicates if a deal desk HR discount applies.
 * @param {boolean} discountDealDeskPayrollImp - Indicates if a deal desk payroll discount applies.
 * @param {boolean} discountSecondEIN - Indicates if a second EIN discount applies.
 * @param {boolean} discountGroupImp - Indicates if a group implementation discount applies.
 * @param {number} discountDealDeskHRImpRate - Deal desk HR implementation discount rate.
 * @param {number} discountDealDeskQuickstartImpRate - Deal desk quickstart implementation discount rate.
 * @param {number} discountDealDeskPayrollImpRate - Deal desk payroll implementation discount rate.
 * @param {number} productAdditionalStorageCount - Additional storage count.
 * @param {number} productAdditionalJobsCount - Additional jobs count.
 * @param {boolean} productHistoricalDataImport - Flag indicating if historical data import is selected.
 * @param {boolean} productMarketplaceListing - Flag indicating if marketplace listing is selected.
 * @param {boolean} productSponsorship - Flag indicating if sponsorship is selected.
 * @param {number} productPaidServiceAmount - Paid service amount.
 * @param {boolean} productTestAccount - Flag indicating if test account is selected.
 * @param {Date|string} activationDate - Date of activation. If not supplied, defaults to 1st day of month.
 * @param {boolean} differenceQuote - Indicates if this is a difference quote.
 * @param {string|number} accountSnowflake - Snowflake ID for the account.
 * @param {number} coreCountryCount - Core country count.
 * @param {any} promotionalDiscounts - Promotional discount details.
 * @param {Date|string} nextInvoiceDate - Date of next invoice.
 * @param {CurrencyObject} currencyObject - Currency object.
 * @param {number} accountBalance - Current balance of the account.
 * @param {number} monthlyTax - Monthly tax amount.
 * @param {any} productEINIntegrations - Payroll EIN integrations.
 * @param {string[]} allPermissions - Permissions for the account.
 * @param {string} user_role - Role of the user.
 * @param {string} account_status - Status of the account.
 * @param {PricebookDetails} pricebookDetails - Pricebook details.
 * @param {CurrencyObject} previousCurrencyObject - Previous currency object.
 * @param {any} productPayrollEINImpTypes - Payroll EIN implementation types.
 * @param {boolean} prepaymentActivationUseDifference - Flag indicating if prepayment activation should use difference.
 * @param {Map<string, number|boolean>} dynamicProducts - Map of dynamic product codes to their selected values/quantities.
 * @param {string} [selectedTimeProduct='timeTracking'] - Which time product is selected ('timeTracking' or 'timeAttendance').
 * @param {boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Threaded through calculateParams to calculateActivationTotalForProduct.
 * @param {boolean} [isRecalculation=false] - Flag indicating if the function is being called for recalculation; when true the function short-circuits and returns 0.
 * @param {boolean} [forceIncludeBundle=false] - Flag indicating if the bundle discount should be included in the calculation even if it normally wouldn't.
 *
 * @returns {number} The total annual price for the quote.
 */
export function calculateAnnualTotal(
   //MonthlyPrice objects for each monthly product which factors into the activation total
   productCoreMonthlyCosts,
   productPerformanceMonthlyCosts,
   productTimeTrackingMonthlyCosts,
   productTimeAttendanceMonthlyCosts,
   productBenefitsAdministrationMonthlyCosts,
   AdditionalJobsCosts,
   AdditionalStorageCosts,
   TestAccountCosts,
   MonthlyTaxCosts,
   TimeClockMonthlyCost,
   payrollMonthlyTotal,
   dynamicMonthlyCosts,
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength,
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate, // if not supplied, defaults to 1st day of month
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   isRecalculation = false,
   forceIncludeBundle = false,
) {
   //Create a Tuple of all the parameters to pass to the calculateQuote function
   // This is a workaround to avoid passing too many parameters directly.
   const calculateParams = [
      product_list,
      discount_list,
      book_id,
      quoteType,
      coreProductSelected,
      productCoreEECount,
      productPerformanceEECount,
      productTimeTrackingEECount,
      hasTimeClocks,
      productTimeClockDetails,
      productBenefitsAdministrationEECount,
      productPayrollEINCount,
      productPayrollEECount,
      productPayrollEECounts,
      productPayrollTotalStateCount,
      productPayrollStateCounts,
      professionalServiceDetails,
      consultingServiceDetails,
      impType,
      benAdminImpType,
      payrollImpType,
      discountBundle,
      discountNonProfit,
      prepayLength,
      discountMultiEntity,
      discountHRMonthlyPromotion,
      discountHRDealDeskMonthly,
      discountReseller,
      discountQuickstartTraining,
      discountImpPromotion,
      discountDealDeskHRImp,
      discountDealDeskPayrollImp,
      discountSecondEIN,
      discountGroupImp,
      discountDealDeskHRImpRate,
      discountDealDeskQuickstartImpRate,
      discountDealDeskPayrollImpRate,
      productAdditionalStorageCount,
      productAdditionalJobsCount,
      productHistoricalDataImport,
      productMarketplaceListing,
      productSponsorship,
      productPaidServiceAmount,
      productTestAccount,
      activationDate, // if not supplied, defaults to 1st day of month
      differenceQuote,
      accountSnowflake,
      coreCountryCount,
      promotionalDiscounts,
      nextInvoiceDate,
      currencyObject,
      accountBalance,
      monthlyTax,
      productEINIntegrations,
      allPermissions,
      user_role,
      account_status,
      pricebookDetails,
      previousCurrencyObject,
      productPayrollEINImpTypes,
      prepaymentActivationUseDifference,
      dynamicProducts,
      selectedTimeProduct,
      isATSOnly,
   ]

   let monthlyProducts = [
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productTimeTrackingMonthlyCosts,
      productTimeAttendanceMonthlyCosts,
      productBenefitsAdministrationMonthlyCosts,
      AdditionalJobsCosts,
      AdditionalStorageCosts,
      TestAccountCosts,
      MonthlyTaxCosts,
      TimeClockMonthlyCost,
   ]
   if (dynamicMonthlyCosts.length > 0) {
      monthlyProducts.push(...dynamicMonthlyCosts)
   }

   if (!IsForeignCurrencyQuote(book_id)) {
      monthlyProducts.push(payrollMonthlyTotal)
   }
   let AnnualTotalPrice = 0
   if (!isRecalculation) {
      for (const monthlyProduct of monthlyProducts) {
         AnnualTotalPrice += calculateActivationTotalForProduct(
            monthlyProduct,
            ...calculateParams,
            12,
            false,
            forceIncludeBundle,
         )
      }
   } else {
      AnnualTotalPrice = 0
   }
   return AnnualTotalPrice
}

/**
 * Calculates the annual total price for payroll only, accounting for time-limited discounts.
 * Uses the same month-by-month recalculation approach as calculateAnnualTotal
 * but only for the payrollMonthlyTotal product.
 *
 * @param {MonthlyPrice} payrollMonthlyTotal - Monthly pricing for payroll.
 * @param {any[]} product_list - Array of available products.
 * @param {Record<string, any>} discount_list - Object mapping discount identifiers to their details.
 * @param {string|number} book_id - Identifier for the book in the system.
 * @param {string} quoteType - The type of quote being calculated.
 * @param {boolean} coreProductSelected - Flag indicating if the core product is selected.
 * @param {number} productCoreEECount - Core employee count for the product.
 * @param {number} productPerformanceEECount - Performance employee count for the product.
 * @param {number} productTimeTrackingEECount - Time tracking employee count for the product.
 * @param {boolean} hasTimeClocks - Flag indicating if time clocks are enabled.
 * @param {any} productTimeClockDetails - Details for time clock configuration.
 * @param {number} productBenefitsAdministrationEECount - Benefits administration employee count.
 * @param {number} productPayrollEINCount - Payroll EIN count.
 * @param {number} productPayrollEECount - Payroll employee count.
 * @param {any} productPayrollEECounts - Additional payroll employee counts.
 * @param {number} productPayrollTotalStateCount - Total state count for payroll.
 * @param {any} productPayrollStateCounts - Payroll state counts.
 * @param {any} professionalServiceDetails - Details for professional services.
 * @param {any} consultingServiceDetails - Details for consulting services.
 * @param {string} impType - Implementation type.
 * @param {string} benAdminImpType - Benefits administration implementation type.
 * @param {string} payrollImpType - Payroll implementation type.
 * @param {any} discountBundle - Bundle discount details.
 * @param {boolean} discountNonProfit - Indicates if a non-profit discount applies.
 * @param {number} prepayLength - Prepayment length in months.
 * @param {boolean} discountMultiEntity - Indicates if a multi-entity discount applies.
 * @param {boolean} discountHRMonthlyPromotion - Indicates if a monthly promotion discount applies.
 * @param {boolean} discountHRDealDeskMonthly - Indicates if a deal desk monthly discount applies.
 * @param {boolean} discountReseller - Indicates if a reseller discount applies.
 * @param {boolean} discountQuickstartTraining - Indicates if a quickstart training discount applies.
 * @param {boolean} discountImpPromotion - Indicates if an implementation promotion discount applies.
 * @param {boolean} discountDealDeskHRImp - Indicates if a deal desk HR discount applies.
 * @param {boolean} discountDealDeskPayrollImp - Indicates if a deal desk payroll discount applies.
 * @param {boolean} discountSecondEIN - Indicates if a second EIN discount applies.
 * @param {boolean} discountGroupImp - Indicates if a group implementation discount applies.
 * @param {number} discountDealDeskHRImpRate - Deal desk HR implementation discount rate.
 * @param {number} discountDealDeskQuickstartImpRate - Deal desk quickstart implementation discount rate.
 * @param {number} discountDealDeskPayrollImpRate - Deal desk payroll implementation discount rate.
 * @param {number} productAdditionalStorageCount - Additional storage count.
 * @param {number} productAdditionalJobsCount - Additional jobs count.
 * @param {boolean} productHistoricalDataImport - Flag indicating if historical data import is selected.
 * @param {boolean} productMarketplaceListing - Flag indicating if marketplace listing is selected.
 * @param {boolean} productSponsorship - Flag indicating if sponsorship is selected.
 * @param {number} productPaidServiceAmount - Paid service amount.
 * @param {boolean} productTestAccount - Flag indicating if test account is selected.
 * @param {Date|string} activationDate - Date of activation.
 * @param {boolean} differenceQuote - Indicates if this is a difference quote.
 * @param {string|number} accountSnowflake - Snowflake ID for the account.
 * @param {number} coreCountryCount - Core country count.
 * @param {any} promotionalDiscounts - Promotional discount details.
 * @param {Date|string} nextInvoiceDate - Date of next invoice.
 * @param {CurrencyObject} currencyObject - Currency object.
 * @param {number} accountBalance - Current balance of the account.
 * @param {number} monthlyTax - Monthly tax amount.
 * @param {any} productEINIntegrations - Payroll EIN integrations.
 * @param {string[]} allPermissions - Permissions for the account.
 * @param {string} user_role - Role of the user.
 * @param {string} account_status - Status of the account.
 * @param {PricebookDetails} pricebookDetails - Pricebook details.
 * @param {CurrencyObject} previousCurrencyObject - Previous currency object.
 * @param {any} productPayrollEINImpTypes - Payroll EIN implementation types.
 * @param {boolean} prepaymentActivationUseDifference - Flag indicating if prepayment activation should use difference.
 * @param {Map<string, number|boolean>} dynamicProducts - Map of dynamic product codes to their selected values/quantities.
 * @param {string} [selectedTimeProduct='timeTracking'] - Which time product is selected ('timeTracking' or 'timeAttendance').
 * @param {boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Threaded through calculateParams to calculateActivationTotalForProduct.
 * @param {boolean} [isRecalculation=false] - Flag indicating if the function is being called for recalculation; when true the function short-circuits and returns 0.
 * @param {boolean} [forceIncludeBundle=false] - Flag indicating if the bundle discount should be forced into the calculation.
 *
 * @returns {number} The total annual price for payroll only.
 */
export function calculatePayrollAnnualTotal(
   payrollMonthlyTotal,
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength,
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate,
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   isRecalculation = false,
   forceIncludeBundle = false,
) {
   const calculateParams = [
      product_list,
      discount_list,
      book_id,
      quoteType,
      coreProductSelected,
      productCoreEECount,
      productPerformanceEECount,
      productTimeTrackingEECount,
      hasTimeClocks,
      productTimeClockDetails,
      productBenefitsAdministrationEECount,
      productPayrollEINCount,
      productPayrollEECount,
      productPayrollEECounts,
      productPayrollTotalStateCount,
      productPayrollStateCounts,
      professionalServiceDetails,
      consultingServiceDetails,
      impType,
      benAdminImpType,
      payrollImpType,
      discountBundle,
      discountNonProfit,
      prepayLength,
      discountMultiEntity,
      discountHRMonthlyPromotion,
      discountHRDealDeskMonthly,
      discountReseller,
      discountQuickstartTraining,
      discountImpPromotion,
      discountDealDeskHRImp,
      discountDealDeskPayrollImp,
      discountSecondEIN,
      discountGroupImp,
      discountDealDeskHRImpRate,
      discountDealDeskQuickstartImpRate,
      discountDealDeskPayrollImpRate,
      productAdditionalStorageCount,
      productAdditionalJobsCount,
      productHistoricalDataImport,
      productMarketplaceListing,
      productSponsorship,
      productPaidServiceAmount,
      productTestAccount,
      activationDate,
      differenceQuote,
      accountSnowflake,
      coreCountryCount,
      promotionalDiscounts,
      nextInvoiceDate,
      currencyObject,
      accountBalance,
      monthlyTax,
      productEINIntegrations,
      allPermissions,
      user_role,
      account_status,
      pricebookDetails,
      previousCurrencyObject,
      productPayrollEINImpTypes,
      prepaymentActivationUseDifference,
      dynamicProducts,
      selectedTimeProduct,
      isATSOnly,
   ]

   if (isRecalculation) {
      return 0
   }

   return calculateActivationTotalForProduct(
      payrollMonthlyTotal,
      ...calculateParams,
      12,
      false,
      forceIncludeBundle,
   )
}
